import _ from "lodash";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ActionPermissionsObject, IWorkspacePlayerSettings, PermissionActionType, WorkspacePlanTier } from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import Banner from "js/Components/Banner";
import { getStaticUrl } from "js/config";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { PresentationPrivacyType } from "js/core/models/presentation";
import { FlexBox } from "js/react/components/LayoutGrid";
import { ShowErrorDialog, ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { UIPane } from "js/react/components/UiComponents";
import { UIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import PlayerSettings from "js/react/views/PresentationSettings/components/PlayerSettings";
import { TeamResourcesPaneHeader } from "js/react/views/TeamResources/components/TeamResourcesPaneHeader";
import { t } from "js/i18n";

const logger = getLogger(LogGroup.WORKSPACES);

const StyledUIPane = styled(UIPane as any)`
    padding: 24px 20px 0;
    display: flex;
    flex-direction: column;
`;

const PlayerSettingsContainer = styled.div`
    background: white;
    padding: 20px;
`;

const StyledResultsContainer = styled(UIPaneResultsContainer)`
    margin-top: 0;
`;

export default WorkspaceController.withInitializedState(
    function PlayerSettingsPane(props: WorkspaceControllerState) {
        const { workspace } = props;
        const [playerSettings, setPlayerSettings] = useState<IWorkspacePlayerSettings>(_.cloneDeep(workspace.player.settings));
        const [fetching, setFetching] = useState(false);

        const canManagePlayerSettings = WorkspaceController.actionPermissions[ActionPermissionsObject.PLAYER_SETTINGS].manage;
        const canUpgradeToManagePlayerSettings = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.PLAYER_SETTINGS, PermissionActionType.MANAGE);
        const isTeamOrEnterprise =
            WorkspaceController.plan.tier === WorkspacePlanTier.TEAM ||
            WorkspaceController.plan.tier === WorkspacePlanTier.ENTERPRISE;
        const isPlayerSettingsLocked = !isTeamOrEnterprise || !canManagePlayerSettings;

        useEffect(() => {
            if (getObjectHash(workspace.player.settings) !== getObjectHash(playerSettings)) {
                setPlayerSettings(_.cloneDeep(workspace.player.settings));
            }
        }, [getObjectHash(workspace.player.settings)]);

        const handleChange = async (delta: Partial<IWorkspacePlayerSettings>) => {
            setFetching(true);

            try {
                // Just in case, you never know what these old components are sending
                const cleanDelta = _.pick(delta, ["showTitle", "showAuthor", "requireEmail", "showGetDeckUI", "allowPdfDownload"]);
                if (Object.keys(cleanDelta).length === 0) {
                    return;
                }

                // Update the UI immediately for smoother UX
                setPlayerSettings({
                    ...playerSettings,
                    ...cleanDelta,
                });

                await WorkspaceController.updateWorkspace({ player: { settings: { ...cleanDelta } } });
            } catch (error) {
                logger.error(error, "[PlayerSettingsPane] Error updating player settings");

                ShowErrorDialog({
                    title: t("Error updating player settings"),
                    message: t("An error occurred while updating the player settings. Please try again later."),
                });

                // Revert the UI to the previous state
                setPlayerSettings(playerSettings);
            } finally {
                setFetching(false);
            }
        };

        return (
            <StyledUIPane>
                <TeamResourcesPaneHeader title={t("Player settings")} />
                {!isTeamOrEnterprise &&
                    <FlexBox center fillWidth padding="0 0px 20px">
                        <Banner
                            headerText={t("Unlock standardized Player Settings")}
                            bodyText={t("Upgrade to customize default player settings for your team.")}
                            buttonLabel={t("Upgrade")}
                            onButtonClick={() => ShowDialog(ChangePlanDialog,
                                {
                                    featuredPlan: WorkspacePlanTier.TEAM,
                                    analytics: { cta: "team_player_settings" }
                                }
                            )}
                        />
                    </FlexBox>
                }
                <StyledResultsContainer>
                    <PlayerSettingsContainer className="player_settings">
                        <PlayerSettings
                            removeBeautifulBranding
                            privacySetting={PresentationPrivacyType.PUBLIC}
                            slideThumbnail={getStaticUrl("/images/thumbnails/sample-slide-large.png")}
                            presentationName={t("Presentation Title")}
                            handleChange={handleChange}
                            showTitle={playerSettings.showTitle}
                            showAuthor={playerSettings.showAuthor}
                            requireEmail={playerSettings.requireEmail}
                            showGetDeckUI={playerSettings.showGetDeckUI}
                            allowPdfDownload={playerSettings.allowPdfDownload}
                            disabled={fetching || isPlayerSettingsLocked || canUpgradeToManagePlayerSettings}
                        />
                    </PlayerSettingsContainer>
                </StyledResultsContainer>
            </StyledUIPane>
        );
    }
);
